import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Calendar,
  IndianRupee,
  Truck,
  AlertCircle,
  CheckCircle2,
  Clock,
  User,
  Package,
  Phone,
  FileText,
  X,
  Loader2,
  AlertTriangle,
  TrendingUp,
  Filter,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rupee = (n) => `₹${(n || 0).toFixed(2)}`;
const todayStr = () => new Date().toISOString().split("T")[0];
const isToday = (dateStr) => dateStr && dateStr === todayStr();
const isPast = (dateStr) => dateStr && dateStr < todayStr();
const isFuture = (dateStr) => dateStr && dateStr > todayStr();

const fmtDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const fmtFull = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

// DeliveryBadge component
function DeliveryBadge({ order }) {
  if (!order.scheduled_delivery_date) return null;

  const isOverdue = isPast(order.scheduled_delivery_date);
  const isDueToday = isToday(order.scheduled_delivery_date);
  const isUpcoming = isFuture(order.scheduled_delivery_date);

  let badgeClass = "bg-amber-500/15 text-amber-400 border-amber-500/30";
  let icon = <Clock className="w-3 h-3" />;
  let label = "Pending";

  if (order.status === "delivered") {
    badgeClass = "bg-emerald-500/15 text-emerald-400 border-emerald-500/30";
    icon = <CheckCircle2 className="w-3 h-3" />;
    label = "Delivered";
  } else if (isOverdue) {
    badgeClass = "bg-rose-500/15 text-rose-400 border-rose-500/30";
    icon = <AlertTriangle className="w-3 h-3" />;
    label = "Overdue";
  } else if (isDueToday) {
    badgeClass = "bg-orange-500/15 text-orange-400 border-orange-500/30";
    icon = <AlertCircle className="w-3 h-3" />;
    label = "Due Today";
  }

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-medium ${badgeClass}`}
    >
      {icon}
      {label}
    </div>
  );
}

// Modal for collecting balance
function CollectBalanceModal({ order, onClose, onSuccess, token }) {
  const { user } = useAuth();
  const [amount, setAmount] = useState(order.balance_due || 0);
  const [processing, setProcessing] = useState(false);

  const handleCollect = async () => {
    try {
      setProcessing(true);

      await axios.patch(
        `${API_URL}/orders/${order.id}`,
        {
          amount_paid: (order.amount_paid || 0) + amount,
          balance_due: Math.max(0, (order.balance_due || 0) - amount),
          status: "delivered",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      toast.success("Payment collected and order delivered!");
      onSuccess();
      onClose();
    } catch (err) {
      toast.error("Failed to collect payment");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-stone-900 rounded-xl border border-stone-800 shadow-2xl max-w-md w-full"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-stone-800">
          <div className="flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-bold text-stone-100">
              Collect Balance
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-stone-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <div className="bg-stone-800 rounded-lg p-3 border border-stone-700">
            <div className="text-sm text-stone-400 mb-1">Customer</div>
            <div className="font-medium text-stone-100">
              {order.customer_name}
            </div>
          </div>

          <div className="bg-stone-800 rounded-lg p-3 border border-stone-700">
            <div className="text-sm text-stone-400 mb-1">Order Total</div>
            <div className="font-bold text-stone-100">{rupee(order.total)}</div>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <div className="text-sm text-amber-400 mb-1">Balance Due</div>
            <div className="font-bold text-amber-400 text-xl">
              {rupee(order.balance_due)}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-stone-300">
              Amount to Collect
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 bg-stone-800 border border-stone-700 rounded-lg text-stone-100 focus:outline-none focus:border-emerald-500"
              min="0"
              max={order.balance_due}
              step="0.01"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 py-2 bg-stone-800 border border-stone-700 rounded-lg text-stone-300 hover:bg-stone-700 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleCollect}
              disabled={processing || amount <= 0}
              className="flex-1 py-2 bg-emerald-600 rounded-lg text-white hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Collect & Deliver
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Order Detail Modal
function OrderDetailModal({ order, onClose, onUpdate }) {
  const { user } = useAuth();
  const [processing, setProcessing] = useState(false);

  const markDelivered = async () => {
    try {
      setProcessing(true);

      await axios.patch(
        `${API_URL}/orders/${order.id}`,
        { status: "delivered" },
        {
          headers: { Authorization: `Bearer ${user.access_token}` },
        },
      );

      toast.success("Order marked as delivered!");
      onUpdate();
      onClose();
    } catch (err) {
      toast.error("Failed to update order");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-stone-900 rounded-xl border border-stone-800 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-stone-800">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-bold text-stone-100">
              Order #{order.invoice_no || order.id}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-stone-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Customer Info */}
          <div className="bg-stone-800 rounded-lg p-4 border border-stone-700">
            <div className="flex items-center gap-2 text-sm font-medium text-stone-300 mb-3">
              <User className="w-4 h-4" />
              Customer Information
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-stone-400 mb-1">Name</div>
                <div className="text-sm font-medium text-stone-100">
                  {order.customer_name}
                </div>
              </div>
              {order.customer_phone && (
                <div>
                  <div className="text-xs text-stone-400 mb-1">Phone</div>
                  <div className="text-sm font-medium text-stone-100">
                    {order.customer_phone}
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs text-stone-400 mb-1">Order Date</div>
                <div className="text-sm font-medium text-stone-100">
                  {fmtFull(order.created_at)}
                </div>
              </div>
              <div>
                <div className="text-xs text-stone-400 mb-1">Status</div>
                <DeliveryBadge order={order} />
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-stone-800 rounded-lg p-4 border border-stone-700">
            <div className="flex items-center gap-2 text-sm font-medium text-stone-300 mb-3">
              <Package className="w-4 h-4" />
              Order Items
            </div>
            <div className="space-y-2">
              {order.items.map((item, idx) => (
                <div
                  key={idx}
                  className="flex justify-between items-center py-2 border-b border-stone-700 last:border-0"
                >
                  <div>
                    <div className="text-sm font-medium text-stone-100">
                      {item.product_name}
                    </div>
                    <div className="text-xs text-stone-400">
                      {item.quantity} × {rupee(item.price)}
                    </div>
                  </div>
                  <div className="text-sm font-bold text-emerald-400">
                    {rupee(item.total)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment & Delivery */}
          <div className="bg-stone-800 rounded-lg p-4 border border-stone-700">
            <div className="flex items-center gap-2 text-sm font-medium text-stone-300 mb-3">
              <IndianRupee className="w-4 h-4" />
              Payment Details
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-stone-400">Total Amount</span>
                <span className="font-bold text-stone-100">
                  {rupee(order.total)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-stone-400">Amount Paid</span>
                <span className="font-medium text-emerald-400">
                  {rupee(order.credit_amount || 0)}
                </span>
              </div>
              {order.balance_due > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-stone-400">Balance Due</span>
                  <span className="font-bold text-amber-400">
                    {rupee(order.balance_due)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Delivery Info */}
          {order.scheduled_delivery_date && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-blue-400 mb-2">
                <Truck className="w-4 h-4" />
                Scheduled Delivery
              </div>
              <div className="text-sm text-blue-300">
                Delivery Date: {fmtDate(order.scheduled_delivery_date)}
              </div>
              {isPast(order.scheduled_delivery_date) && (
                <div className="mt-2 text-xs text-rose-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  This delivery is overdue!
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        {order.status !== "delivered" && (
          <div className="p-4 border-t border-stone-800 flex gap-2">
            {order.balance_due > 0 ? (
              <button
                onClick={() => {
                  onClose();
                  // This would open the CollectBalanceModal
                }}
                className="flex-1 py-2 bg-emerald-600 rounded-lg text-white hover:bg-emerald-500 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <IndianRupee className="w-4 h-4" />
                Collect Balance
              </button>
            ) : (
              <button
                onClick={markDelivered}
                disabled={processing}
                className="flex-1 py-2 bg-emerald-600 rounded-lg text-white hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Mark as Delivered
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default function PendingPayments() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [collectBalanceOrder, setCollectBalanceOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(res.data);
    } catch (err) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  // Filter pending/delivery orders
  const pendingOrders = useMemo(() => {
    let filtered = orders.filter(
      (o) =>
        o.status === "partial" ||
        o.status === "pending_delivery" ||
        o.status === "partial",
    );

    // Search by customer name
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((o) =>
        o.customer_name.toLowerCase().includes(q),
      );
    }

    // Filter by status
    if (filterStatus !== "all") {
      if (filterStatus === "overdue") {
        filtered = filtered.filter(
          (o) => o.scheduled_delivery_date && isPast(o.scheduled_delivery_date),
        );
      } else if (filterStatus === "today") {
        filtered = filtered.filter(
          (o) =>
            o.scheduled_delivery_date && isToday(o.scheduled_delivery_date),
        );
      } else if (filterStatus === "upcoming") {
        filtered = filtered.filter(
          (o) =>
            o.scheduled_delivery_date && isFuture(o.scheduled_delivery_date),
        );
      } else if (filterStatus === "partial") {
        filtered = filtered.filter((o) => o.balance_due > 0);
      } else if (filterStatus === "full_paid") {
        filtered = filtered.filter(
          (o) => o.balance_due === 0 || !o.balance_due,
        );
      }
    }

    return filtered.sort((a, b) => {
      // Sort by delivery date, overdue first
      if (a.scheduled_delivery_date && b.scheduled_delivery_date) {
        return a.scheduled_delivery_date.localeCompare(
          b.scheduled_delivery_date,
        );
      }
      if (a.scheduled_delivery_date) return -1;
      if (b.scheduled_delivery_date) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [orders, searchQuery, filterStatus]);

  // Statistics
  const stats = useMemo(() => {
    const total = pendingOrders.length;
    const overdue = pendingOrders.filter(
      (o) => o.scheduled_delivery_date && isPast(o.scheduled_delivery_date),
    ).length;
    const today = pendingOrders.filter(
      (o) => o.scheduled_delivery_date && isToday(o.scheduled_delivery_date),
    ).length;
    const partialPayment = pendingOrders.filter(
      (o) => o.balance_due > 0,
    ).length;
    const totalBalanceDue = pendingOrders.reduce(
      (sum, o) => sum + (o.balance_due || 0),
      0,
    );

    return { total, overdue, today, partialPayment, totalBalanceDue };
  }, [pendingOrders]);

  return (
    <div className="min-h-screen bg-stone-950 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <h1 className="text-2xl font-bold text-stone-100 mb-2">
          Pending Payments & Deliveries
        </h1>
        <p className="text-stone-400">
          Manage credit payments and scheduled deliveries
        </p>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-stone-400 text-sm mb-1">
            <Package className="w-4 h-4" />
            Total Pending
          </div>
          <div className="text-2xl font-bold text-stone-100">{stats.total}</div>
        </div>

        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-rose-400 text-sm mb-1">
            <AlertTriangle className="w-4 h-4" />
            Overdue
          </div>
          <div className="text-2xl font-bold text-rose-400">
            {stats.overdue}
          </div>
        </div>

        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-orange-400 text-sm mb-1">
            <Clock className="w-4 h-4" />
            Due Today
          </div>
          <div className="text-2xl font-bold text-orange-400">
            {stats.today}
          </div>
        </div>

        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-amber-400 text-sm mb-1">
            <IndianRupee className="w-4 h-4" />
            Partial Payments
          </div>
          <div className="text-2xl font-bold text-amber-400">
            {stats.partialPayment}
          </div>
        </div>

        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-emerald-400 text-sm mb-1">
            <TrendingUp className="w-4 h-4" />
            Total Due
          </div>
          <div className="text-xl font-bold text-emerald-400">
            {rupee(stats.totalBalanceDue)}
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                <input
                  type="text"
                  placeholder="Search by customer name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-stone-800 border border-stone-700 rounded-lg text-stone-100 placeholder-stone-500 focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>

            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              {[
                { key: "all", label: "All", icon: Package },
                { key: "overdue", label: "Overdue", icon: AlertTriangle },
                { key: "today", label: "Today", icon: Clock },
                { key: "upcoming", label: "Upcoming", icon: Calendar },
                { key: "partial", label: "Partial Payment", icon: IndianRupee },
                { key: "full_paid", label: "Full Paid", icon: CheckCircle2 },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setFilterStatus(key)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    filterStatus === key
                      ? "bg-emerald-600 text-white"
                      : "bg-stone-800 text-stone-300 hover:bg-stone-700"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Orders List */}
      <div className="max-w-7xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : pendingOrders.length === 0 ? (
          <div className="bg-stone-900 border border-stone-800 rounded-lg p-12 text-center">
            <Package className="w-12 h-12 text-stone-600 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-stone-400 mb-1">
              No pending orders
            </h3>
            <p className="text-sm text-stone-500">
              {searchQuery
                ? "Try adjusting your search"
                : "All orders are completed"}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {pendingOrders.map((order) => (
              <div
                key={order.id}
                className="bg-stone-900 border border-stone-800 rounded-lg p-4 hover:border-emerald-500/50 transition-colors cursor-pointer"
                onClick={() => setSelectedOrder(order)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-stone-100">
                        {order.customer_name}
                      </h3>
                      <DeliveryBadge order={order} />
                    </div>
                    <div className="text-sm text-stone-400">
                      Order #{order.invoice_no || order.id} ·{" "}
                      {fmtFull(order.created_at)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-emerald-400">
                      {rupee(order.total)}
                    </div>
                    {order.balance_due > 0 && (
                      <div className="text-sm text-amber-400">
                        Due: {rupee(order.balance_due)}
                      </div>
                    )}
                  </div>
                </div>

                {order.scheduled_delivery_date && (
                  <div className="flex items-center gap-2 text-sm text-stone-400">
                    <Calendar className="w-4 h-4" />
                    Delivery: {fmtDate(order.scheduled_delivery_date)}
                    {isPast(order.scheduled_delivery_date) && (
                      <span className="text-rose-400 text-xs">
                        (
                        {Math.ceil(
                          (new Date() -
                            new Date(order.scheduled_delivery_date)) /
                            (1000 * 60 * 60 * 24),
                        )}{" "}
                        days overdue)
                      </span>
                    )}
                  </div>
                )}

                {order.items && order.items.length > 0 && (
                  <div className="mt-2 text-xs text-stone-500">
                    {order.items.length} item(s) ·{" "}
                    {order.items
                      .slice(0, 2)
                      .map((i) => i.product_name)
                      .join(", ")}
                    {order.items.length > 2 &&
                      ` +${order.items.length - 2} more`}
                  </div>
                )}

                {order.balance_due > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCollectBalanceOrder(order);
                    }}
                    className="mt-3 w-full py-2 bg-emerald-600 rounded-lg text-white hover:bg-emerald-500 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                  >
                    <IndianRupee className="w-4 h-4" />
                    Collect Balance ({rupee(order.balance_due)})
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {selectedOrder && (
          <OrderDetailModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onUpdate={fetchOrders}
          />
        )}
        {collectBalanceOrder && (
          <CollectBalanceModal
            order={collectBalanceOrder}
            onClose={() => setCollectBalanceOrder(null)}
            onSuccess={fetchOrders}
            token={token}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
